import React, { useState } from "react";
import AgentsCrumb from "./AgentsCrumb";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import AgentSelect from "./AgentSelect";
import DayOfWeekField, { DEFAULT_DAYS, scheduleText, defaultTimezone } from "./DayOfWeekField";
import SequencePickerModal from "./SequencePickerModal";
import { FALLBACK_INDUSTRIES, EMPLOYEE_RANGE_IDS } from "../DeepResearch/components/prospectFilterVocab";

const INDUSTRY_OPTIONS = FALLBACK_INDUSTRIES.map(x => ({ value: x, label: x }));
const EMPLOYEE_OPTIONS = EMPLOYEE_RANGE_IDS.map(x => ({ value: x, label: x }));
const MAX_SEEDS = 5;

// Count non-blank, comma-separated seeds so we can warn past the provider's 5-seed match cap.
const seedCount = raw => raw.split(",").map(s => s.trim()).filter(Boolean).length;

const CreateLookalikeProspector = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [seeds, setSeeds] = useState("");
  const [industries, setIndustries] = useState("");
  const [employeeCount, setEmployeeCount] = useState("");
  const [companyLocation, setCompanyLocation] = useState("");
  const [titles, setTitles] = useState("");
  const [perCompany, setPerCompany] = useState(5);
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [seqModalOpen, setSeqModalOpen] = useState(false);
  const [runWhen, setRunWhen] = useState("DAILY");
  const [time, setTime] = useState("08:00");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [dailyCap, setDailyCap] = useState(25);
  const [notifyMode, setNotifyMode] = useState("DAILY_DIGEST");
  const [saving, setSaving] = useState(false);
  const timezone = defaultTimezone();

  const nSeeds = seedCount(seeds);
  const canSubmit = nSeeds > 0 && !!sequenceId;

  const buildPayload = () => ({
    name: name.trim() || "Lookalike prospector",
    seeds: seeds.trim() || undefined,
    industries: industries.trim() || undefined,
    employeeCount: employeeCount.trim() || undefined,
    companyLocation: companyLocation.trim() || undefined,
    titles: titles.trim() || undefined,
    perCompany: Number(perCompany),
    sequenceId,
    dailyCap: Number(dailyCap),
    notifyMode,
    timezone,
    ...(runWhen === "MANUAL" ? {} : { frequency: runWhen, time, ...(runWhen === "WEEKLY" ? { days } : {}) })
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createLookalikeProspector(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  // Open this template's steps in the canvas to tweak before creating.
  const customize = async () => {
    setSaving(true);
    const draft = await agentsData.composeTemplate("lookalike-prospector", buildPayload());
    setSaving(false);
    if (draft) { agentsData.stageDraft(draft); history.push("/agents/new/custom"); }
  };

  const step = node => (<li><span className="ck">✓</span><span>{node}</span></li>);
  const runText = runWhen === "MANUAL" ? "when you run it" : scheduleText(runWhen, time, days, timezone);

  return (
    <div className="ag-page">
      <div className="ag-page__head">
        <div>
          <AgentsCrumb items={[{ label: "Hire an agent", to: "/agents/new" }, { label: "Set up" }]} />
          <h1 className="ag-page__title">Hire · Lookalike companies → people</h1>
          <p className="ag-page__sub">Give us up to 5 of your best accounts. We find companies that look like them, get the people who work there, and enroll them.</p>
        </div>
      </div>

      <div className="ag-setup">
        <div className="ag-card-panel">
          <div className="ag-fsec">Seed accounts</div>
          <label className="ag-field">
            <span className="ag-field__label">Similar to these companies<b className="ag-req">required</b></span>
            <textarea className="ag-input" rows={3} value={seeds}
              placeholder="e.g. stripe.com, plaid.com, linkedin.com/company/adyen"
              onChange={e => setSeeds(e.target.value)} />
            <span className="ag-hint">
              Up to {MAX_SEEDS} companies, a website domain or a LinkedIn company URL each, comma-separated.
              {nSeeds > MAX_SEEDS && <b> Using the first {MAX_SEEDS} of {nSeeds}, we match up to {MAX_SEEDS}.</b>}
            </span>
          </label>

          <div className="ag-fsec">Narrow the matches <span className="ag-hint" style={{ fontWeight: 400 }}>(optional)</span></div>
          <div className="ag-field-row">
            <label className="ag-field"><span className="ag-field__label">Industry</span>
              <AgentSelect searchable placeholder="Any industry" searchPlaceholder="Search industries…"
                value={industries} onChange={setIndustries} options={INDUSTRY_OPTIONS} />
            </label>
            <label className="ag-field"><span className="ag-field__label">Employee size</span>
              <AgentSelect placeholder="Any size" value={employeeCount} onChange={setEmployeeCount} options={EMPLOYEE_OPTIONS} />
            </label>
          </div>
          <label className="ag-field">
            <span className="ag-field__label">Locations</span>
            <input className="ag-input" value={companyLocation} placeholder="e.g. United States, London" onChange={e => setCompanyLocation(e.target.value)} />
            <span className="ag-hint">Comma-separated. Optional filters combine with your seeds to tighten the match.</span>
          </label>

          <div className="ag-fsec">Which people</div>
          <label className="ag-field">
            <span className="ag-field__label">Job titles</span>
            <input className="ag-input" value={titles} placeholder="e.g. VP Sales, Head of Marketing, Founder" onChange={e => setTitles(e.target.value)} />
            <span className="ag-hint">Comma-separated. Who to find at each company (leave blank for anyone).</span>
          </label>
          <label className="ag-field">
            <span className="ag-field__label">People per company</span>
            <input className="ag-input" type="number" min="1" max="25" value={perCompany} onChange={e => setPerCompany(e.target.value)} />
          </label>
          <label className="ag-field">
            <span className="ag-field__label">Agent name</span>
            <input className="ag-input" value={name} placeholder="Lookalike prospector" onChange={e => setName(e.target.value)} />
          </label>

          <div className="ag-fsec">What happens next</div>
          <label className="ag-field">
            <span className="ag-field__label">Enroll into sequence<b className="ag-req">required</b></span>
            <button type="button" className="ag-select__btn" onClick={() => setSeqModalOpen(true)}>
              <span className={sequenceName ? "" : "ag-select__ph"}>{sequenceName || "Select a sequence…"}</span>
              <svg className="ag-select__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </button>
            <span className="ag-hint">People are deduped and undeliverables dropped first; email sequences enrich the email before enrolling.</span>
          </label>

          <div className="ag-fsec">Schedule</div>
          <div className="ag-field-row">
            <label className="ag-field"><span className="ag-field__label">Run</span>
              <AgentSelect value={runWhen} onChange={setRunWhen} options={[
                { value: "DAILY", label: "Every day" },
                { value: "WEEKDAYS", label: "Weekdays (Mon–Fri)" },
                { value: "WEEKLY", label: "Specific days" },
                { value: "MANUAL", label: "Only when I run it" }
              ]} />
            </label>
            {runWhen === "WEEKLY" && <DayOfWeekField value={days} onChange={setDays} />}
            {runWhen !== "MANUAL" && (
              <label className="ag-field"><span className="ag-field__label">At</span>
                <input className="ag-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
              </label>
            )}
            <label className="ag-field"><span className="ag-field__label">Contacts / run</span>
              <input className="ag-input" type="number" min="1" value={dailyCap} onChange={e => setDailyCap(e.target.value)} />
            </label>
          </div>

          <div className="ag-fsec">Notifications</div>
          <label className="ag-field">
            <span className="ag-field__label">Notify me</span>
            <AgentSelect value={notifyMode} onChange={setNotifyMode} options={[
              { value: "DAILY_DIGEST", label: "Daily digest, one summary a day" },
              { value: "ON_FAILURE", label: "Only when a run fails" },
              { value: "OFF", label: "Off, don't email me" }
            ]} />
          </label>
        </div>

        <div className="ag-rail">
          <div className="ag-summary">
            <h4>⚡ What this agent does</h4>
            <ul className="ag-slist">
              {step(<span>Finds companies like your {nSeeds > 0 ? <b>{Math.min(nSeeds, MAX_SEEDS)} seed{Math.min(nSeeds, MAX_SEEDS) > 1 ? "s" : ""}</b> : "seed accounts"}, {runText}</span>)}
              {step(<span>Gets up to <b>{perCompany}</b> {titles.trim() ? <b>{titles.trim()}</b> : "people"} at each company</span>)}
              {step(<span>Skips already-processed / DNC / undeliverable, enriches emails</span>)}
              {sequenceId && step(<span>Enrolls into <b>{sequenceName}</b></span>)}
              {notifyMode !== "OFF" && step(<span>{notifyMode === "DAILY_DIGEST" ? "Emails you a daily digest" : "Emails you when a run fails"}</span>)}
            </ul>
          </div>
          <button className="ag-btn ag-btn--primary" style={{ justifyContent: "center" }} disabled={!canSubmit || saving} onClick={submit}><i className="material-icons-outlined">check</i>
            {saving ? "Creating…" : "Create agent"}
          </button>
          <button className="ag-btn" style={{ justifyContent: "center" }} disabled={saving} onClick={customize}><i className="material-icons-outlined">tune</i>Customize steps</button>
          <button className="ag-btn" style={{ justifyContent: "center" }} onClick={() => history.push("/agents")}><i className="material-icons-outlined">close</i>Cancel</button>
          {!canSubmit && <p className="ag-hint" style={{ textAlign: "center" }}>Add at least one seed company and pick a sequence to create, or “Customize steps” to edit the flow first.</p>}
        </div>
      </div>

      <SequencePickerModal
        open={seqModalOpen}
        currentId={sequenceId}
        onSelect={s => { setSequenceId(s.id); setSequenceName(s.name); }}
        onClose={() => setSeqModalOpen(false)}
      />
    </div>
  );
});

export default CreateLookalikeProspector;
